/**
 * CallDetail — per-call replay (W4-B).
 *
 * Outer chrome restyled to v3; inner views (TrackView, FlowchartView,
 * AuditLogView) keep their existing rendering — they have specialized
 * SVG/positioning logic that isn't blocked by the design refresh.
 */
import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useCallEvents } from "../hooks/useCallEvents";
import { AnomalyBanner } from "../components/audit/AnomalyBanner";
import { TrackView } from "../components/audit/TrackView";
import { FlowchartView } from "../components/audit/FlowchartView";
import { AuditLogView } from "../components/audit/AuditLogView";
import type { SessionDetail } from "../api/voice-sessions";
import { Card, Mono, Pill, LiveDot } from "../components/v3";

interface Selection {
  ts_start_ms: number;
  ts_end_ms: number;
  source: "track" | "flowchart" | "audit";
  stage?: string;
}

function formatDuration(sec: number | null): string {
  if (sec === null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}m ${s}s`;
}

function statusTone(s: string): "amber" | "ok" | "rose" | "warn" | "neutral" {
  return ({
    active: "amber",
    starting: "amber",
    ended: "neutral",
    failed: "rose",
    dropped: "warn",
  } as const)[s] ?? "neutral";
}

function outcomeTone(o: string | null): "ok" | "rose" | "warn" | "neutral" {
  if (o === "success") return "ok";
  if (o === "failure") return "rose";
  if (o === "incomplete") return "warn";
  return "neutral";
}

function CallDetailHeader({
  session,
  isLive,
}: {
  session: SessionDetail;
  isLive: boolean;
}) {
  return (
    <Card padding="md" glow={isLive}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-2">
            {isLive && <LiveDot />}
            <Pill tone={statusTone(session.status)}>
              {isLive ? "Live" : session.status}
            </Pill>
            <Pill tone={session.direction === "inbound" ? "plum" : "amber"}>
              {session.direction}
            </Pill>
            {session.task_outcome && (
              <Pill tone={outcomeTone(session.task_outcome)}>
                {session.task_outcome}
              </Pill>
            )}
          </div>

          <div className="font-display text-2xl leading-tight">
            <span className="text-t1">
              {session.caller_number ?? "Unknown"}
            </span>
            <span className="text-t3 mx-3">→</span>
            <span className="text-t2">
              {session.callee_number ?? "Unknown"}
            </span>
          </div>

          <div className="flex flex-wrap gap-x-4 gap-y-1 mt-3 text-xs">
            <Mono>{new Date(session.started_at).toLocaleString()}</Mono>
            <Mono>·</Mono>
            <Mono tone="t2">
              Duration {formatDuration(session.duration_seconds)}
            </Mono>
            <Mono>·</Mono>
            <Mono tone="t2">{session.channel ?? "voice"}</Mono>
          </div>
        </div>

        <div className="flex flex-wrap gap-2 shrink-0">
          {session.workflow_id && (
            <Pill tone="neutral">
              {session.workflow_id} v{session.workflow_version}
            </Pill>
          )}
          {session.recording && <Pill tone="neutral">Recorded</Pill>}
          {session.recap && <Pill tone="neutral">Recap sent</Pill>}
        </div>
      </div>
    </Card>
  );
}

function LoadingState() {
  return (
    <div className="space-y-4">
      <div className="skeleton h-24" />
      <div className="skeleton h-32" />
      <div className="skeleton h-64" />
    </div>
  );
}

function ErrorState({ error }: { error: Error | null }) {
  return (
    <Card padding="lg">
      <div className="text-rose font-display text-xl">
        {error?.message ?? "Failed to load call data"}
      </div>
    </Card>
  );
}

function BackLink() {
  return (
    <Link
      to="/app/calls"
      className="inline-flex items-center gap-1.5 text-xs text-t3 hover:text-t1 transition-colors"
    >
      <span aria-hidden="true">←</span>
      <span className="mono-caps">Back to calls</span>
    </Link>
  );
}

export function CallDetail() {
  const { id } = useParams<{ id: string }>();
  const { session, events, isLoading, error, isLive } = useCallEvents(id ?? "");
  const [selection, setSelection] = useState<Selection | null>(null);

  if (isLoading)
    return (
      <div className="px-8 py-6 max-w-[1280px] mx-auto">
        <BackLink />
        <div className="mt-4">
          <LoadingState />
        </div>
      </div>
    );

  if (error || !session)
    return (
      <div className="px-8 py-6 max-w-[1280px] mx-auto">
        <BackLink />
        <div className="mt-4">
          <ErrorState error={error} />
        </div>
      </div>
    );

  const startedAtMs = new Date(session.started_at).getTime();
  const endedAtMs = session.ended_at
    ? new Date(session.ended_at).getTime()
    : Date.now();
  const totalDurationMs = Math.max(endedAtMs - startedAtMs, 1);

  const handleFlowchartSelect = (stage: string) => {
    const stageEvents = events.filter(
      (e) => e.stage === stage || e.payload?.["stage"] === stage,
    );
    if (stageEvents.length === 0) {
      setSelection(null);
      return;
    }
    setSelection({
      ts_start_ms: stageEvents[0]!.ts_ms,
      ts_end_ms: stageEvents[stageEvents.length - 1]!.ts_ms,
      source: "flowchart",
      stage,
    });
  };

  return (
    <div className="px-8 py-6 max-w-[1280px] mx-auto space-y-4">
      <BackLink />

      <AnomalyBanner
        anomalies={session.anomalies}
        allRulesSatisfied={session.all_rules_satisfied}
      />

      <CallDetailHeader session={session} isLive={isLive} />

      <Card padding="md">
        <Mono className="block mb-3">Timeline</Mono>
        <TrackView
          events={events}
          workflow={session.workflow_definition}
          totalDurationMs={totalDurationMs}
          startedAtMs={startedAtMs}
          selection={selection}
          onSelect={(range) =>
            setSelection(range ? { ...range, source: "track" } : null)
          }
        />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" style={{ minHeight: 520 }}>
        <Card padding="md">
          <Mono className="block mb-3">Call flow</Mono>
          <FlowchartView
            workflow={session.workflow_definition}
            stagesVisited={session.stages_visited}
            stagesSkipped={session.stages_skipped}
            currentStage={session.current_stage}
            selection={selection}
            onSelect={handleFlowchartSelect}
          />
        </Card>

        <Card padding="md" className="flex flex-col">
          <Mono className="block mb-3">Event log</Mono>
          <div className="flex-1 min-h-0">
            <AuditLogView
              events={events}
              startedAtMs={startedAtMs}
              selection={selection}
              onSelect={(range) =>
                setSelection(range ? { ...range, source: "audit" } : null)
              }
            />
          </div>
        </Card>
      </div>

      {session.transcript_summary && (
        <Card padding="md">
          <Mono className="block mb-3">Summary</Mono>
          <p className="text-t1 text-[15px] leading-relaxed font-display italic"
            style={{ fontVariationSettings: '"opsz" 36, "SOFT" 80' }}
          >
            {session.transcript_summary}
          </p>
        </Card>
      )}
    </div>
  );
}
